import React, { useState, useEffect } from 'react';
import { nacmeLogo } from './assets/images';

const FilmScreening = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filmToken, setFilmToken] = useState('');

  const FILM_ID = 'moses-13-steps';
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem('mosesFilmToken');
      const storedEmail = localStorage.getItem('mosesFilmEmail');

      if (storedToken && storedEmail) {
        try {
          const response = await fetch(
            `${API_BASE}/api/film/${FILM_ID}/check-session?token=${encodeURIComponent(storedToken)}`,
            { method: 'GET' }
          );

          if (response.ok) {
            setFilmToken(storedToken);
            setEmail(storedEmail);
            setAuthenticated(true);
          } else {
            // Session invalid, clear storage
            localStorage.removeItem('mosesFilmToken');
            localStorage.removeItem('mosesFilmEmail');
          }
        } catch (err) {
          console.error('Session check failed:', err);
          localStorage.removeItem('mosesFilmToken');
          localStorage.removeItem('mosesFilmEmail');
        }
      }
    };

    checkSession();
  }, [API_BASE]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/film/verify-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          password: password,
          film_id: FILM_ID
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      // Store session
      localStorage.setItem('mosesFilmToken', data.token);
      localStorage.setItem('mosesFilmEmail', email.trim());

      setFilmToken(data.token);
      setAuthenticated(true);
    } catch (err) {
      setError(err.message || 'Failed to authenticate. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('mosesFilmToken');
    localStorage.removeItem('mosesFilmEmail');
    setAuthenticated(false);
    setEmail('');
    setPassword('');
    setFilmToken('');
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center px-4 py-8">
        <div className="film-grain"></div>

        <div className="relative z-10 max-w-md w-full">
          {/* Title */}
          <div className="text-center mb-6 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-serif text-gold mb-2 tracking-wide">
              MOSES
            </h1>
            <p className="text-xl md:text-2xl text-gold-light font-light tracking-widest">
              13 STEPS
            </p>
            <div className="mt-4 h-px bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <p className="mt-4 text-cream text-sm uppercase tracking-wider">
              Private Screening
            </p>
          </div>

          {/* NACME Logo */}
          <div className="text-center mb-6 animate-fade-in">
            <img
              src={nacmeLogo}
              alt="NACME Logo"
              className="h-10 w-auto mx-auto drop-shadow-sm"
            />
          </div>

          {/* Login Form */}
          <div className="bg-black bg-opacity-80 backdrop-blur-sm border border-gold border-opacity-30 rounded-lg p-8 shadow-2xl">
            <h2 className="text-3xl font-serif text-center mb-2" style={{
              background: 'linear-gradient(to right, #d4af37, #f4d38f)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Welcome to NACME Searchlight
            </h2>
            <h3 className="text-xl text-cream font-serif text-center mb-6">
              Enter the Theatre
            </h3>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm mb-2" style={{ color: '#f5f5dc' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border rounded focus:outline-none transition-colors"
                  style={{
                    backgroundColor: '#1a1a1a',
                    borderColor: '#4a4a4a',
                    color: '#ffffff'
                  }}
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm mb-2" style={{ color: '#f5f5dc' }}>
                  Access Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border rounded focus:outline-none transition-colors"
                  style={{
                    backgroundColor: '#1a1a1a',
                    borderColor: '#4a4a4a',
                    color: '#ffffff'
                  }}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="bg-red-900 bg-opacity-50 border border-red-600 rounded p-3 text-red-200 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full font-semibold py-3 px-6 rounded transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                style={{
                  background: loading ? '#8b7355' : 'linear-gradient(to right, #d4af37, #f4d38f)',
                  color: '#000000',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.target.style.background = 'linear-gradient(to right, #f4d38f, #d4af37)';
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.target.style.background = 'linear-gradient(to right, #d4af37, #f4d38f)';
                }}
              >
                {loading ? 'Verifying...' : 'Begin Screening'}
              </button>
            </form>

            <p className="mt-6 text-center text-gray-400 text-xs">
              This is a private screening. Access expires October 25, 2025.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Viewing Experience
  return (
    <div className="min-h-screen bg-black text-cream">
      {/* Film grain overlay */}
      <div className="film-grain"></div>

      {/* Logout button */}
      <button
        onClick={handleLogout}
        className="fixed top-4 left-4 z-50 text-gray-400 hover:text-gold transition-colors text-sm"
      >
        ← Exit Screening
      </button>

      {/* Content */}
      <div className="relative z-10">
        {/* Title Section */}
        <div className="text-center pt-12 md:pt-16 px-4 animate-fade-in">
          <h1 className="text-4xl md:text-6xl font-serif text-gold mb-2 tracking-wide">
            MOSES
          </h1>
          <p className="text-xl md:text-3xl text-gold-light font-light tracking-widest mb-2">
            13 STEPS
          </p>
          <div className="max-w-md mx-auto mt-4 h-px bg-gradient-to-r from-transparent via-gold to-transparent"></div>
        </div>

        {/* Video Player */}
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
          <div className="relative rounded-lg overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src="https://www.youtube.com/embed/RovU9vvemAU?si=xRs8rV3jefbYgw3w"
              title="MOSES - 13 Steps"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>

        {/* Film Description */}
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-8 md:py-12">
          <div className="prose prose-invert prose-lg max-w-none">
            <h2 className="text-2xl md:text-3xl font-serif text-gold mb-6">
              MOSES<br />
              <span className="text-xl text-gold-light">13 Steps</span>
            </h2>

            <p className="text-cream leading-relaxed text-base md:text-lg mb-4">
              MOSES - 13 STEPS tells the inspiring life story of one of the most extraordinary athletes in the history of sport, Edwin Moses.
            </p>

            <p className="text-cream leading-relaxed text-base md:text-lg mb-4">
              From his childhood in segregated America and his formative years at Morehouse College in Atlanta, to his astonishing victory at the 1976 Olympics and his furious gold medal in 1984, to his post-athletic career, the film illuminates Moses' journey - as an athlete, scientist, engineer and humanitarian, as a pioneer and visionary leader, and as an advocate of equality, fairness and integrity in sport.
            </p>

            <p className="text-cream leading-relaxed text-base md:text-lg mb-4">
              Moses finds his discipline rather by chance and teaches himself the hardest discipline in athletics, the 400 meter hurdles. A historic winning streak follows: 9 years, 9 months and 9 days unbeaten.
            </p>

            <p className="text-cream leading-relaxed text-base md:text-lg mb-4">
              Even during his career, the Olympic champion fought for stricter doping controls, fair pay for athletes and equal rights.
            </p>

            <p className="text-cream leading-relaxed text-base md:text-lg">
              Exclusive interviews with Edwin Moses himself, with icons such as Spike Lee, Samuel L. Jackson and Neil deGrasse Tyson as well as sports greats such as Karsten Warholm, Tommie Smith and Michael Johnson provide very personal insights into the thoughts, motivations and challenges of the exceptional athlete Edwin Moses.
            </p>
          </div>
        </div>

        {/* Filmmaker Info */}
        <div className="max-w-2xl mx-auto px-6 md:px-8 pb-12 md:pb-16">
          <div className="border-t border-gray-800 pt-8">
            <h3 className="text-lg font-serif text-gold-light mb-4">About the Filmmaker</h3>
            <div className="text-gray-400 italic text-sm md:text-base leading-relaxed">
              <p className="font-semibold text-gray-300 mb-2">BROADVIEW TV</p>
              <p>
                Founded in 1999. The company's success is based on its specialization in documentaries, its collaboration with award-winning authors and the high proportion of its own financing in the productions, which enables it to distribute its own rights downstream. Own compositions commissioned by BROADVIEW Musikverlag round off the films.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .film-grain {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0.03;
          background-image:
            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.05) 2px, rgba(255, 255, 255, 0.05) 4px),
            repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 255, 255, 0.05) 2px, rgba(255, 255, 255, 0.05) 4px);
          pointer-events: none;
          z-index: 1;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out;
        }

        .text-gold {
          color: #d4af37;
        }

        .text-gold-light {
          color: #f4d38f;
        }

        .bg-gold {
          background-color: #d4af37;
        }

        .bg-gold-light {
          background-color: #f4d38f;
        }

        .text-cream {
          color: #f5f5dc;
        }

        .border-gold {
          border-color: #d4af37;
        }

        .focus\\:border-gold:focus {
          border-color: #d4af37;
        }

        .focus\\:ring-gold:focus {
          --tw-ring-color: #d4af37;
        }

        .from-gold {
          --tw-gradient-from: #d4af37;
        }

        .to-gold-light {
          --tw-gradient-to: #f4d38f;
        }

        .from-gold-light {
          --tw-gradient-from: #f4d38f;
        }

        .to-gold {
          --tw-gradient-to: #d4af37;
        }

        .via-gold {
          --tw-gradient-stops: var(--tw-gradient-from), #d4af37, var(--tw-gradient-to);
        }
      `}</style>
    </div>
  );
};

export default FilmScreening;
